import { reducedVfx } from "../ui/settings";

/**
 * Hit-stop and the slow-motion ramp after it (art_review/vfx.md F8). A request freezes the world for a few tens of
 * milliseconds — sim time and animation mixers crawl at 5 %, particles at 25 %, the camera stays live so the shake
 * still reads — which is what makes a blow land. The level loop multiplies its dt by `step(dt).sim` BEFORE feeding
 * the fixed-tick accumulator: the sim still steps in whole 20 Hz ticks of unchanged length, it just gets fewer of
 * them for a moment, so determinism and replays are untouched (it is the same as the frame arriving late).
 *
 * At most one stop per `Cooldown` (a chain of kills must not turn into a stutter); `force` (the boss death) ignores
 * that. `ramp` follows the stop with `RampSeconds` of slow motion easing from `RampFrom` back to 1×. Under Reduced
 * effects or the OS reduce-motion setting there is none at all (WP10, S4): the freeze and the slow-motion ramp are
 * motion effects like the shake, and several callers (kills, SAVED!, the boss's fall) did not check for themselves.
 */
export interface TimeScales { sim: number; fx: number }

export class HitStop {
  static readonly SimScale = 0.05;
  static readonly FxScale = 0.25;
  static readonly Cooldown = 0.4;
  static readonly RampSeconds = 0.4;
  static readonly RampFrom = 0.4;
  /**
   * Durations in ms per beat. There is no mortar tower; the cannon's splash stands in for the review's mortar. `saved`
   * is a kill at the gate (level/moments.ts SAVED!), requested with the slow-motion ramp so the save is felt.
   */
  static readonly Ms = { baseStrike: 70, bossHeavyHit: 50, splashMultiKill: 40, bossDeath: 250, saved: 60 } as const;

  private stopLeft = 0;
  private rampLeft = 0;
  private sinceLast = Infinity;
  private readonly out: TimeScales = { sim: 1, fx: 1 };

  /** `reduced`: whether motion is to be kept down (ui/settings reducedVfx; injectable for tests). */
  constructor(private readonly reduced: () => boolean = reducedVfx) {}

  /** Asks for a stop of `ms`. Returns false when refused by the cooldown. */
  request(ms: number, { ramp = false, force = false }: { ramp?: boolean; force?: boolean } = {}): boolean {
    if (!(ms > 0) || this.reduced()) return false;
    if (!force && this.sinceLast < HitStop.Cooldown) return false;
    this.stopLeft = Math.max(this.stopLeft, ms / 1000);
    this.sinceLast = 0;
    if (ramp) this.rampLeft = HitStop.RampSeconds;
    return true;
  }

  get active(): boolean { return this.stopLeft > 0 || this.rampLeft > 0; }

  /** Advances by real seconds and returns this frame's multipliers (the same object each call). */
  step(dt: number): TimeScales {
    this.sinceLast += dt;
    const o = this.out;
    if (this.stopLeft > 0) {
      this.stopLeft -= dt;
      o.sim = HitStop.SimScale; o.fx = HitStop.FxScale;
    } else if (this.rampLeft > 0) {
      const k = 1 - this.rampLeft / HitStop.RampSeconds;
      o.sim = o.fx = HitStop.RampFrom + (1 - HitStop.RampFrom) * k * k * (3 - 2 * k);
      this.rampLeft -= dt;
    } else {
      o.sim = 1; o.fx = 1;
    }
    return o;
  }

  reset(): void { this.stopLeft = 0; this.rampLeft = 0; this.sinceLast = Infinity; }
}
